/* eslint-disable react-hooks/exhaustive-deps */
import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  CircularProgress,
  Alert,
  Paper,
  IconButton,
  FormControlLabel,
  Checkbox,
  Autocomplete,
} from "@mui/material";
import { BrandButton } from "components/brand";
import {
  ArrowBack,
  CloudUpload,
  Delete,
  Image as ImageIcon,
} from "@mui/icons-material";
import {
  createProduct,
  updateProduct,
  getProductById,
  getAllMenuCategories,
  getAllProductsWithoutPagination,
} from "actions";
import { clearError, clearCurrentProduct } from "features/productsSlice";
import { RichTextEditor, cleanRichTextForSave } from "components";
import { PATHNAME } from "constants/pathnames";
import Notify from "helpers/Notify";
import { NOTIFY } from "constants/notifyType";
import { ADMIN_TEXTS } from "constants/adminTexts";
import { FILE_INPUT_ACCEPT } from "constants/fileTypes";
import { SPINNER_SIZE } from "constants/general";
import "./Products.scss";

const imageBase = process.env.REACT_APP_API_IMAGE_URL;

const AUTOCOMPLETE_HALF_SX = { width: "calc(50% - 8px)" };

const validationSchema = Yup.object({
  title: Yup.string()
    .required(ADMIN_TEXTS.products.titleRequired)
    .min(3, ADMIN_TEXTS.products.titleMinLength)
    .max(100, ADMIN_TEXTS.products.titleMaxLength),
  description: Yup.string()
    .required(ADMIN_TEXTS.products.descriptionRequired)
    .test("min-length", ADMIN_TEXTS.products.descriptionMinLength, (value) => {
      const textContent = value?.replace(/<[^>]*>/g, "").trim();
      return textContent && textContent.length >= 10;
    })
    .test("max-length", ADMIN_TEXTS.products.descriptionMaxLength, (value) => {
      const textContent = value?.replace(/<[^>]*>/g, "").trim();
      return !textContent || textContent.length <= 2000;
    }),
  price: Yup.number()
    .typeError(ADMIN_TEXTS.products.priceMustBeNumber)
    .required(ADMIN_TEXTS.products.priceRequired)
    .min(0, ADMIN_TEXTS.products.priceMinValue),
});

const ProductForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const isEdit = Boolean(id);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [isVideo, setIsVideo] = useState(false);
  const [imageError, setImageError] = useState(false);

  const { currentProduct, loading, error } = useSelector(
    (state) => state.products,
  );
  const categories = useSelector((state) => state.menuCategories.categories);

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      price: "",
      isMenuSliderItem: false,
      categoryId: null,
      subcategoryId: null,
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!imagePreview) {
        setImageError(true);
        return;
      }

      try {
        const cleanedDescription = cleanRichTextForSave(
          values.description || "",
        );

        const productData = {
          ...values,
          description: cleanedDescription,
          image: imageFile,
        };

        if (isEdit) {
          await dispatch(updateProduct({ id, productData })).unwrap();
        } else {
          await dispatch(createProduct(productData)).unwrap();
        }
        dispatch(getAllProductsWithoutPagination());
        navigate(PATHNAME.products);
      } catch (error) {
        Notify(NOTIFY.type.error, error?.response?.data?.message || "");
      }
    },
  });

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageFile(file);
      setIsVideo(file?.type?.startsWith("video"));
      setImageError(false);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setIsVideo(false);
    setImageError(false);
  };

  const handleBack = () => {
    navigate(PATHNAME.products);
  };

  useEffect(() => {
    if (isEdit && id) {
      dispatch(getProductById(id));
    }
    return () => {
      dispatch(clearCurrentProduct());
    };
  }, [id, isEdit]);

  const handleCategoryChange = (_, newValue) => {
    formik.setFieldValue("categoryId", newValue?.id ?? null);
    formik.setFieldValue("subcategoryId", null);
  };

  const selectedCategory = categories.find(
    (c) => c.id === formik.values.categoryId,
  );
  const hasSubcategories = (selectedCategory?.subcategories?.length ?? 0) > 0;

  useEffect(() => {
    if (categories.length === 0) {
      dispatch(getAllMenuCategories());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (currentProduct && isEdit) {
      formik.setValues({
        title: currentProduct.title || "",
        description: currentProduct.description || "",
        price: currentProduct.price ?? "",
        isMenuSliderItem: currentProduct.isMenuSliderItem ?? false,
        categoryId: currentProduct.category?.id ?? null,
        subcategoryId: currentProduct.subcategory?.id ?? null,
      });
      if (currentProduct.image) {
        const imagePath = `${imageBase}${currentProduct.image.path}`;
        setImagePreview(imagePath);
        setIsVideo(currentProduct?.image?.mimeType?.startsWith("video"));
      }
    }
  }, [currentProduct, isEdit]);

  useEffect(() => {
    if (error) {
      dispatch(clearError());
    }
  }, [error]);

  if (loading && isEdit) {
    return (
      <Box className="loading-container">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="product-form">
      <Box className="form-header">
        <IconButton onClick={handleBack} className="back-button">
          <ArrowBack />
        </IconButton>
        <Typography variant="h4" component="h1" fontWeight="bold">
          {isEdit ? ADMIN_TEXTS.products.edit : ADMIN_TEXTS.products.create}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" className="error-alert">
          {error}
        </Alert>
      )}

      <Card>
        <CardContent>
          <form onSubmit={formik.handleSubmit}>
            <Box display="flex" flexDirection="column" gap={3}>
              <Box className="image-upload-section">
                <Typography variant="h6" gutterBottom>
                  {ADMIN_TEXTS.products.productImage} *
                </Typography>
                <Paper
                  className={`upload-paper${imageError ? " upload-paper--error" : ""}`}
                >
                  {imagePreview ? (
                    <Box>
                      <Box className="preview-container">
                        {isVideo ? (
                          <video
                            src={imagePreview}
                            className="preview-image"
                            controls
                          />
                        ) : (
                          <img
                            src={imagePreview}
                            alt="Preview"
                            className="preview-image"
                          />
                        )}
                        <IconButton
                          onClick={handleRemoveImage}
                          className="remove-button"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                      <BrandButton
                        variant="secondary"
                        component="label"
                        startIcon={<CloudUpload />}
                        className="change-image-button"
                      >
                        {ADMIN_TEXTS.products.changeImage}
                        <input
                          type="file"
                          hidden
                          accept={FILE_INPUT_ACCEPT.MEDIA}
                          onChange={handleImageChange}
                        />
                      </BrandButton>
                    </Box>
                  ) : (
                    <Box>
                      <ImageIcon className="upload-icon" />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        className="upload-text"
                      >
                        {ADMIN_TEXTS.products.uploadImage}
                      </Typography>
                      <BrandButton
                        variant="secondary"
                        component="label"
                        startIcon={<CloudUpload />}
                      >
                        {ADMIN_TEXTS.products.chooseImage}
                        <input
                          type="file"
                          hidden
                          accept={FILE_INPUT_ACCEPT.MEDIA}
                          onChange={handleImageChange}
                        />
                      </BrandButton>
                    </Box>
                  )}
                </Paper>
                {imageError && (
                  <Typography
                    variant="caption"
                    color="error"
                    className="image-error-text"
                  >
                    {ADMIN_TEXTS.products.productImageRequired}
                  </Typography>
                )}
              </Box>

              <TextField
                fullWidth
                label={ADMIN_TEXTS.products.productTitleAsterisk}
                name="title"
                value={formik.values.title}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.title && Boolean(formik.errors.title)}
                helperText={formik.touched.title && formik.errors.title}
                placeholder={ADMIN_TEXTS.products.enterTitle}
              />

              <RichTextEditor
                value={formik.values.description}
                onChange={(value) => formik.setFieldValue("description", value)}
                onBlur={() => formik.setFieldTouched("description", true)}
                placeholder={ADMIN_TEXTS.products.enterDescription}
                label={ADMIN_TEXTS.products.productDescription}
                required
                error={formik.errors.description}
                touched={formik.touched.description}
              />

              <TextField
                fullWidth
                label={ADMIN_TEXTS.products.priceAsterisk}
                name="price"
                type="number"
                value={formik.values.price}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.price && Boolean(formik.errors.price)}
                helperText={formik.touched.price && formik.errors.price}
                placeholder={ADMIN_TEXTS.products.enterPrice}
                inputProps={{ min: 0, step: "any" }}
                className="price-field"
              />

              <Box display="flex" gap={2} flexWrap="wrap">
                <Autocomplete
                  sx={AUTOCOMPLETE_HALF_SX}
                  options={categories}
                  getOptionLabel={(option) => option.name}
                  value={selectedCategory ?? null}
                  onChange={handleCategoryChange}
                  isOptionEqualToValue={(option, value) =>
                    option.id === value.id
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={ADMIN_TEXTS.products.categoryLabel}
                    />
                  )}
                />

                {hasSubcategories && (
                  <Autocomplete
                    sx={AUTOCOMPLETE_HALF_SX}
                    options={selectedCategory.subcategories}
                    getOptionLabel={(option) => option.name}
                    value={
                      selectedCategory.subcategories.find(
                        (s) => s.id === formik.values.subcategoryId,
                      ) ?? null
                    }
                    onChange={(_, newValue) =>
                      formik.setFieldValue("subcategoryId", newValue?.id ?? null)
                    }
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={ADMIN_TEXTS.products.subcategoryLabel}
                      />
                    )}
                  />
                )}
              </Box>

              <FormControlLabel
                control={
                  <Checkbox
                    name="isMenuSliderItem"
                    checked={formik.values.isMenuSliderItem}
                    onChange={formik.handleChange}
                  />
                }
                label={ADMIN_TEXTS.menuSlider.inMenuSlider}
              />

              <Box className="form-actions">
                <BrandButton
                  variant="secondary"
                  onClick={handleBack}
                  disabled={loading}
                >
                  {ADMIN_TEXTS.common.cancel}
                </BrandButton>
                <BrandButton
                  type="submit"
                  variant="primary"
                  disabled={loading || !formik.isValid}
                >
                  {loading ? (
                    <CircularProgress size={SPINNER_SIZE} color="inherit" />
                  ) : isEdit ? (
                    ADMIN_TEXTS.products.updateProduct
                  ) : (
                    ADMIN_TEXTS.products.createProduct
                  )}
                </BrandButton>
              </Box>
            </Box>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProductForm;
